/**
 * PromoteScratchDialog — promote an ephemeral `scratch` snapshot into
 * a real `:Project` with a first `:SchematicVersion(v1)`. Per IR plan
 * §8.6 (`promote_circuit`).
 *
 * The actual promotion runs through `commit_design` once the user
 * provides the project name — no separate backend endpoint needed.
 * Existing `useCommitSnapshot` hook handles the rest; this dialog
 * builds the right payload (creating an empty Project first when
 * needed is a Phase 14 follow-up, currently surfaces a guidance Alert).
 */
import { useCommitSnapshot } from "@features/circuits/hooks/useCircuitIr";
import PromoteIcon from "@mui/icons-material/UpgradeRounded";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "@stores/toastStore";
import { useState } from "react";

interface PromoteScratchDialogProps {
  open: boolean;
  /** The scratch snapshot to promote. */
  snapshotId: string | null;
  onClose: () => void;
  onPromoted?: (versionId: string) => void;
}

export function PromoteScratchDialog({
  open,
  snapshotId,
  onClose,
  onPromoted,
}: PromoteScratchDialogProps) {
  // Body is unmounted while ``open`` is false (MUI Dialog default), so the
  // next open starts with fresh useState initial values — no reset useEffect.
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <PromoteScratchDialogBody
        snapshotId={snapshotId}
        onClose={onClose}
        onPromoted={onPromoted}
      />
    </Dialog>
  );
}

interface PromoteScratchDialogBodyProps {
  snapshotId: string | null;
  onClose: () => void;
  onPromoted?: (versionId: string) => void;
}

function PromoteScratchDialogBody({
  snapshotId,
  onClose,
  onPromoted,
}: PromoteScratchDialogBodyProps) {
  const [projectName, setProjectName] = useState("");
  const [description, setDescription] = useState("");
  const commit = useCommitSnapshot();

  function handlePromote() {
    if (!snapshotId) return;
    const trimmed = projectName.trim();
    if (!trimmed) {
      toast.error("Project name required");
      return;
    }
    commit.mutate(
      {
        snapshotId,
        // The scratch's project doesn't exist yet — backend `commit_design`
        // currently expects an existing :Project. Phase 14 adds an
        // optional `create_project` flag; until then the user must
        // create the project first via Upload (or via the agent). We
        // pass the name as the project_id stand-in so the round-trip
        // is consistent once the backend gains the flag.
        project_id: trimmed,
        description: description.trim() || undefined,
        set_current: true,
        drc_gate: true,
      },
      {
        onSuccess: (data) => {
          onPromoted?.(data.version_id);
          onClose();
        },
      },
    );
  }

  return (
    <>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PromoteIcon color="primary" fontSize="small" />
          <Box>Promote scratch to project</Box>
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
          Pin this scratch's snapshot chain (no GC), materialise the first
          version in the project's history, and run the refdes annotation pass
          so AI labels become real designators.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Project name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            placeholder="e.g. PowerControlBoard"
            autoFocus
            required
            fullWidth
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What does this circuit do?"
            multiline
            minRows={2}
            fullWidth
          />
          <Alert severity="info" variant="outlined">
            On promote: status flips ephemeral → draft → active, the snapshot
            becomes v1 of the project, and refdes labels (`R?1`, `R?2`, …) get
            assigned real numbers.
          </Alert>
          {commit.error && (
            <Alert severity="error">
              {(commit.error as Error).message ?? "Could not promote snapshot."}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={commit.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<PromoteIcon />}
          onClick={handlePromote}
          disabled={!snapshotId || !projectName.trim() || commit.isPending}
        >
          {commit.isPending ? "Promoting…" : "Promote"}
        </Button>
      </DialogActions>
    </>
  );
}
